import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, isToday, isThisWeek, parseISO, differenceInDays } from 'date-fns';
import { CheckCircle2, Circle, Calendar, TrendingUp, Play, Flame, Bell } from 'lucide-react';
import { getTasks, saveTasks, getSubjects, getStreakData, updateStreakData } from '../utils/storage';
import FocusMode from './FocusMode';

const TaskList = ({ period }) => {
  const [tasks, setTasks] = useState([]);
  const [subjects, setSubjects] = useState({});
  const [focusModeTask, setFocusModeTask] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    const tasksData = getTasks();
    const subjectsData = getSubjects();
    const subjectsMap = subjectsData.reduce((acc, subj) => {
      acc[subj.id] = { ...subj };
      return acc;
    }, {});
    setSubjects(subjectsMap);

    let filteredTasks = tasksData.filter(task => {
      const deadline = parseISO(task.deadline);
      if (period === 'daily') {
        return !task.completed && (isToday(deadline) || deadline < new Date());
      } else {
        return !task.completed && (isThisWeek(deadline) || deadline < new Date());
      }
    });

    // Sort tasks by priority and urgency for daily view
    if (period === 'daily') {
      filteredTasks.sort((a, b) => {
        const aDeadline = parseISO(a.deadline);
        const bDeadline = parseISO(b.deadline);
        const now = new Date();

        // First, prioritize overdue tasks
        const aOverdue = aDeadline < now;
        const bOverdue = bDeadline < now;
        if (aOverdue && !bOverdue) return -1;
        if (!aOverdue && bOverdue) return 1;

        // Then prioritize today's tasks
        const aToday = isToday(aDeadline);
        const bToday = isToday(bDeadline);
        if (aToday && !bToday) return -1;
        if (!aToday && bToday) return 1;

        // Then sort by priority (high -> medium -> low)
        const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
        const aPriority = priorityOrder[a.priority || 'medium'];
        const bPriority = priorityOrder[b.priority || 'medium'];
        if (aPriority !== bPriority) return bPriority - aPriority;

        // Finally, sort by deadline (earliest first)
        return aDeadline - bDeadline;
      });
    }

    setTasks(filteredTasks);
  };

  const toggleComplete = (taskId) => {
    const allTasks = getTasks();
    const taskIndex = allTasks.findIndex(t => t.id === taskId);
    if (taskIndex >= 0) {
      const wasCompleted = allTasks[taskIndex].completed;
      allTasks[taskIndex].completed = !wasCompleted;
      if (!wasCompleted) {
        // Task is being completed
        allTasks[taskIndex].completedAt = new Date().toISOString();
      } else {
        // Task is being uncompleted
        delete allTasks[taskIndex].completedAt;
      }
      saveTasks(allTasks);

      // Update streak data
      const todayTasks = getTasks().filter(task => {
        const deadline = parseISO(task.deadline);
        return isToday(deadline) || deadline < new Date();
      });
      const completedToday = todayTasks.filter(t => t.completed).length;
      const totalToday = todayTasks.length;
      updateStreakData(completedToday === totalToday && totalToday > 0);

      loadData();
    }
  };

  const getTaskStatus = (task) => {
    const deadline = parseISO(task.deadline);
    const now = new Date();

    if (task.completed) return 'completed';
    if (deadline < now) return 'overdue';
    if (isToday(deadline)) return 'urgent';
    if (differenceInDays(deadline, now) <= 2) return 'upcoming';
    return 'normal';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'var(--priority-high)';
      case 'medium': return 'var(--priority-medium)';
      case 'low': return 'var(--priority-low)';
      default: return 'var(--priority-medium)';
    }
  };

  const todayTasks = period === 'daily' ? getTasks().filter(task => {
    const deadline = parseISO(task.deadline);
    return isToday(deadline) || deadline < new Date();
  }) : [];
  const completedToday = todayTasks.filter(t => t.completed).length;
  const totalToday = todayTasks.length;

  const getMotivationalMessage = () => {
    if (totalToday === 0) return "Ready for a productive day!";
    if (completedToday === 0) return "Let's get started!";
    if (completedToday === totalToday) return "Amazing work today! 🎉";
    if (completedToday / totalToday > 0.8) return "Almost there!";
    if (completedToday / totalToday > 0.5) return "Great progress!";
    return "Keep going!";
  };

  return (
    <div className="container">
      <div className="header-section">
        <h1>{period === 'daily' ? "Today's Focus" : 'Weekly Tasks'}</h1>
        {period === 'daily' && (
          <div className="focus-mode-indicator">
            <TrendingUp size={16} />
            <span>{getMotivationalMessage()}</span>
          </div>
        )}
      </div>

      {period === 'daily' && (
        <>
          <div className="streak-card">
            <div className="streak-header">
              <Flame size={20} className="streak-icon" />
              <span className="streak-label">Study Streak</span>
            </div>
            <div className="streak-number">{getStreakData().currentStreak}</div>
            <div className="streak-subtitle">days in a row</div>
          </div>

          {totalToday > 0 && (
            <div className="progress-card">
              <div className="progress-header">
                <h3>Daily Progress</h3>
                <span className="progress-text">{completedToday} of {totalToday} completed</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${(completedToday / totalToday) * 100}%`,
                    background: `linear-gradient(90deg, var(--success) 0%, var(--primary) 100%)`
                  }}
                ></div>
              </div>
            </div>
          )}
        </>
      )}

      {tasks.length > 0 ? (
        <div className="tasks-list">
          {tasks.map(task => {
            const status = getTaskStatus(task);
            const subject = subjects[task.subjectId];
            const priorityColor = getPriorityColor(task.priority);

            return (
              <div
                key={task.id}
                className={`task-card ${status} ${task.priority || 'medium'}`}
                style={{
                  '--subject-color': subject?.color || '#64748b',
                  '--priority-color': priorityColor
                }}
              >
                <div className="task-header">
                  <button
                    className="task-checkbox"
                    onClick={() => toggleComplete(task.id)}
                    aria-label={task.completed ? 'Mark as incomplete' : 'Mark as complete'}
                  >
                    {task.completed ? (
                      <CheckCircle2 size={24} className="check-icon" />
                    ) : (
                      <Circle size={24} className="circle-icon" />
                    )}
                  </button>

                  <div className="task-content">
                    <h3 className={`task-title ${task.completed ? 'completed' : ''}`}>
                      {task.title}
                    </h3>

                    <div className="task-meta">
                      {subject && (
                        <span
                          className="subject-badge"
                          style={{ backgroundColor: subject.color + '20', color: subject.color }}
                        >
                          {subject.name}
                        </span>
                      )}

                      <div className="deadline-info">
                        <Calendar size={14} />
                        <span className={`deadline-text ${status}`}>
                          {status === 'overdue' && 'Overdue: '}
                          {status === 'urgent' && 'Due today: '}
                          {format(parseISO(task.deadline), 'MMM dd')}
                          {status === 'upcoming' && ` (${differenceInDays(parseISO(task.deadline), new Date())} days)`}
                        </span>
                      </div>

                      {task.reminderTime && (
                        <div className="deadline-info">
                          <Bell size={14} />
                          <span className="deadline-text">
                            Reminder: {task.reminderTime}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="task-actions">
                    <button
                      className="focus-mode-btn"
                      onClick={() => setFocusModeTask(task.id)}
                      title="Start Focus Mode"
                    >
                      <Play size={16} />
                    </button>
                    <div className="priority-indicator" style={{ backgroundColor: priorityColor }}>
                      <span className="priority-label">
                        {task.priority?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                {task.description && (
                  <p className="task-description">{task.description}</p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-illustration">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="20" y="40" width="80" height="50" rx="8" fill="currentColor" opacity="0.1"/>
              <rect x="30" y="50" width="60" height="6" rx="3" fill="currentColor" opacity="0.3"/>
              <rect x="30" y="60" width="40" height="6" rx="3" fill="currentColor" opacity="0.2"/>
              <rect x="30" y="70" width="50" height="6" rx="3" fill="currentColor" opacity="0.2"/>
              <circle cx="85" cy="75" r="8" fill="currentColor" opacity="0.4"/>
              <path d="M82 75l3 3 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2>No {period} tasks</h2>
          <p>You're all caught up! Ready for a productive session?</p>
          <Link to="/add-task">
            <button className="add-button">
              <Circle size={20} />
              Add Your First Task
            </button>
          </Link>
        </div>
      )}

      {tasks.length > 0 && (
        <Link to="/add-task">
          <button className="add-button secondary">
            Add Another Task
          </button>
        </Link>
      )}

      {focusModeTask && (
        <FocusMode
          taskId={focusModeTask}
          onClose={() => {
            setFocusModeTask(null);
            loadData(); // Refresh data when focus mode closes
          }}
        />
      )}
    </div>
  );
};

export default TaskList;
